import React, { useEffect, useMemo, useState } from 'react';
import { Controller } from 'react-hook-form';
import { format } from 'date-fns';
import { faExternalLink, faCopy } from '@fortawesome/pro-regular-svg-icons';
import { faCircle } from '@fortawesome/pro-solid-svg-icons';
import OutlookLogo from '/images/outlook.svg';
import {
    ConnectionAction,
    ConnectionStatus,
    emailsAndDomainsRegex,
    getAvailableActions,
} from '../../../document/constants';
import FormField from '../../../core/components/FormField';
import SelectField from '../../../core/components/Fields/SelectField';
import classnames from '../../../core/utils/classnames';
import Button from '../../../core/components/Button';
import ConfirmModal from '../../../core/components/ConfirmModal';
import { withIcon } from '../../../core/components/Icon';
import { useMutation } from '@apollo/client';
import { GET_ADMIN_CONSENT_URL, SETUP_PERSONAL_MAILBOX } from '../../queries';
import { useToaster } from '../../../core/components/Toast';
import { PersonalSmartInboxModal } from './PersonalSmartInboxModal';
import { DELETE_SMART_INBOX_INTEGRATION } from '../../../customer/queries';
import TagsField from '../../../core/components/Fields/TagsField';

const ExternalLinkIcon = withIcon(faExternalLink);
const DotIcon = withIcon(faCircle);
const CopyIcon = withIcon(faCopy);

interface PersonalSmartInboxIntegrationProps {
    translate: (key: string, options?: Record<string, unknown>) => string;
    authForm: any;
    channelForm: any;
    excludedContentForm: any;
    connectOutlook: any;
    refetchPersonalMailboxStatus: () => Promise<void>;
    isConnected: boolean;
    connectedChannels: string[];
    personalSmartInboxId?: string;
    connectionFailedAt?: string | null;
    isDisconnecting: boolean;
    isAdminConsentGranted: boolean;
    isAdminConsentUpToDate: boolean;
    setIsDisconnecting: (v: boolean) => void;
    isModalOpen: boolean;
    hasErrorIntegration: boolean;
    setHasErrorIntegration: (v: boolean) => void;
    searchParams: URLSearchParams;
    setSearchParams: any;
    handleOpenModal: () => void;
    handleCloseModal: () => void;
    handleDisconnect: (form: any) => void;
    handlePause: (form: any) => void;
    handleResume: (form: any) => void;
    connectedAgentName?: string | null;
    sectionClassnames?: string;
    onSaveSuccess?: () => void;
}

export const PersonalSmartInboxIntegration: React.FC<PersonalSmartInboxIntegrationProps> = ({
    translate,
    authForm,
    channelForm,
    excludedContentForm,
    connectOutlook,
    refetchPersonalMailboxStatus,
    isConnected,
    connectedChannels,
    personalSmartInboxId,
    connectionFailedAt,
    isDisconnecting,
    isAdminConsentGranted,
    isAdminConsentUpToDate,
    setIsDisconnecting,
    isModalOpen,
    hasErrorIntegration,
    setHasErrorIntegration,
    searchParams,
    setSearchParams,
    handleOpenModal,
    handleCloseModal,
    handleDisconnect,
    handlePause,
    handleResume,
    sectionClassnames,
    connectedAgentName,
    onSaveSuccess,
}) => {
    const [setupPersonalMailbox] = useMutation(SETUP_PERSONAL_MAILBOX);
    const [getAdminConsentUrl] = useMutation(GET_ADMIN_CONSENT_URL);
    const [deleteSmartInboxIntegration] = useMutation(DELETE_SMART_INBOX_INTEGRATION);
    const [statusChanging, setStatusChanging] = useState(false);
    const [adminConsentLink, setAdminConsentLink] = useState<string>('');
    const { publishToast } = useToaster();

    const runMutation = async (fn: () => Promise<any>, errorKey: string) => {
        setStatusChanging(true);
        try {
            await fn();
        } catch (error) {
            console.error(error);
            publishToast({ description: translate(errorKey), status: 'error' });
        } finally {
            setStatusChanging(false);
        }
    };

    const setConnectionStatus = async (status: boolean) => {
        await runMutation(
            () =>
                setupPersonalMailbox({
                    variables: { enabled: status, integrationId: personalSmartInboxId },
                }),
            'personalSmartInbox.errorConnectionStatus'
        );
    };

    const handleSaveExcludedContent = async (formData) => {
        setStatusChanging(true);
        try {
            await setupPersonalMailbox({
                variables: {
                    manualProcessingKeywords: formData.excludedContent.keywords ?? [],
                    manualProcessingSenderEmails: formData.excludedContent.emailsDomains ?? [],
                    integrationId: personalSmartInboxId,
                },
            });
            publishToast({
                description: translate('personalSmartInbox.excludedContentForm.saveSuccess'),
                status: 'success',
            });
            onSaveSuccess?.();
        } catch (error) {
            console.error(error);
            publishToast({
                description: translate('personalSmartInbox.excludedContentForm.saveError'),
                status: 'error',
            });
        } finally {
            setStatusChanging(false);
        }
    };

    const disconnectMailbox = async () => {
        await runMutation(async () => {
            await deleteSmartInboxIntegration({
                variables: { id: personalSmartInboxId },
            });
            await refetchPersonalMailboxStatus();
        }, 'personalSmartInbox.errorConnectionStatus');
    };

    const handleStatusChange = async (action) => {
        if (action === ConnectionAction.Disconnect) {
            setIsDisconnecting(true);
        } else if (action === ConnectionAction.Pause) {
            handlePause(authForm);
            await setConnectionStatus(false);
        } else if (action === ConnectionAction.Resume) {
            handleResume(authForm);
            await setConnectionStatus(true);
        }
    };

    const needsAdminConsentAction = !isAdminConsentGranted || !isAdminConsentUpToDate;

    useEffect(() => {
        const fetchAdminConsentUrl = async () => {
            if (!needsAdminConsentAction) return;

            try {
                const { data } = await getAdminConsentUrl({
                    variables: { isPersonalMailbox: true },
                });
                if (data?.getAdminConsentUrl?.adminConsentUrl) {
                    setAdminConsentLink(data.getAdminConsentUrl.adminConsentUrl);
                }
            } catch (error) {
                console.error('Failed to fetch admin consent URL:', error);
                publishToast({
                    description: translate('personalSmartInbox.errorFetchingAdminConsentUrl'),
                    status: 'error',
                });
            }
        };

        fetchAdminConsentUrl();
    }, [needsAdminConsentAction, getAdminConsentUrl, publishToast, translate]);

    const groupedChannels = useMemo(() => {
        return connectedChannels.reduce(
            (acc, channel) => {
                const [type, name] = Object.entries(channel)[0] || [];
                if (type && name) {
                    acc[type] = acc[type] || [];
                    acc[type].push(name);
                }
                return acc;
            },
            {} as Record<string, string[]>
        );
    }, [connectedChannels]);

    const handleCopyLink = async () => {
        try {
            await navigator.clipboard.writeText(adminConsentLink);
            publishToast({ description: translate('personalSmartInbox.linkCopied'), status: 'success' });
        } catch (error) {
            console.error('Failed to copy link:', error);
            publishToast({ description: translate('personalSmartInbox.linkCopyFailed'), status: 'error' });
        }
    };

    return (
        <section
            className={classnames(
                'w-full max-w-[48rem] flex flex-col border border-solid border-line-weak bg-surface-weak rounded-lg p-8 gap-6',
                sectionClassnames
            )}
        >
            <div className="w-full flex flex-col gap-4 md:flex-row md:gap-0">
                <div className="rounded-lg border border-solid border-line-weak h-12 w-12 p-[7px] flex items-center">
                    <img src={OutlookLogo} alt="Outlook logo" />
                </div>

                <div className="flex-2 md:ml-3 flex flex-col md:self-center">
                    <div>
                        <span className="font-semibold text-sm text-content-strong">
                            {translate('personalSmartInbox.title')}
                        </span>
                    </div>
                    <span className="font-regular text-sm text-content-medium">
                        {translate('personalSmartInbox.description')}
                    </span>
                </div>

                {/* Connection statuses */}
                <div className="md:self-center">
                    {isConnected && isAdminConsentGranted ? (
                        <Controller
                            name="status"
                            control={authForm.control}
                            render={({ field }) => {
                                const currentStatus = field.value as ConnectionStatus;
                                const isActiveStatus =
                                    currentStatus === ConnectionStatus.Connected ||
                                    currentStatus === ConnectionStatus.Paused;
                                // get corresponding action based on current status
                                const availableActions = getAvailableActions(currentStatus);
                                const actionOptions = availableActions.map((a) => ({ label: a, value: a }));
                                return (
                                    <FormField className="flex-1">
                                        <SelectField
                                            isLoading={statusChanging}
                                            value={currentStatus}
                                            options={actionOptions}
                                            required
                                            className={classnames(
                                                isActiveStatus && 'bg-surface-brand-medium-default',
                                                currentStatus === ConnectionStatus.Error && 'bg-surface-danger-weak'
                                            )}
                                            renderOption={(option) => (
                                                <span className="text-content-strong">
                                                    {translate(`personalSmartInbox.${option.label.toLowerCase()}`)}
                                                </span>
                                            )}
                                            renderValue={() => (
                                                <span
                                                    className={classnames(
                                                        'text-content-strong font-medium text-sm flex items-center',
                                                        isActiveStatus && 'text-content-brand-medium'
                                                    )}
                                                >
                                                    <DotIcon
                                                        className={classnames(
                                                            'text-[10px] pr-2 pb-[1px]',
                                                            currentStatus === ConnectionStatus.Connected &&
                                                                'text-content-success-weak',
                                                            currentStatus === ConnectionStatus.Paused &&
                                                                'text-content-warning-weak',
                                                            currentStatus === ConnectionStatus.Disconnected &&
                                                                'text-content-weak',
                                                            currentStatus === ConnectionStatus.Error &&
                                                                'text-content-danger-weak'
                                                        )}
                                                    />
                                                    {translate(`personalSmartInbox.${currentStatus.toLowerCase()}`)}
                                                </span>
                                            )}
                                            onValueChange={async (action) => {
                                                handleStatusChange(action);
                                            }}
                                        />
                                    </FormField>
                                );
                            }}
                        />
                    ) : (
                        <Button
                            disabled={!isAdminConsentGranted}
                            variant="ghost"
                            onClick={handleOpenModal}
                            className="flex items-center h-8 font-medium text-sm px-2 border border-line-weak"
                        >
                            {translate('personalSmartInbox.connectToOutlook')}
                            <ExternalLinkIcon className="pl-2" />
                        </Button>
                    )}
                </div>
            </div>
            {connectionFailedAt && (
                <div className="w-full max-w-[520px] rounded-sm bg-surface-danger-weak p-3">
                    <span className="font-regular text-sm text-content-danger-strong">
                        {translate('personalSmartInbox.connectionErrorHint', {
                            date: format(new Date(connectionFailedAt), 'PP'),
                        })}
                    </span>
                </div>
            )}
            {needsAdminConsentAction && (
                <div className="w-full max-w-[520px]">
                    <span className="mt-4 font-regular text-sm text-content-medium">
                        {translate(
                            isAdminConsentGranted
                                ? 'personalSmartInbox.adminConsentOutdatedInstruction'
                                : 'personalSmartInbox.adminConsentInstruction'
                        )}
                    </span>
                    <div className="mt-4 justify-between flex items-center gap-2 bg-surface-medium p-2 rounded-sm">
                        <span className="font-regular text-sm truncate max-w-[400px] text-content-strong">
                            {adminConsentLink}
                        </span>
                        <Button
                            variant="ghost"
                            onClick={handleCopyLink}
                            className="flex items-center gap-1 px-2 py-1 h-auto text-xs cursor-pointer"
                        >
                            <CopyIcon className="text-sm hover:bg-transparent" />
                            {translate('personalSmartInbox.copyLink')}
                        </Button>
                    </div>
                </div>
            )}

            {isAdminConsentGranted && connectedChannels.length > 0 && (
                <div className="w-full">
                    <span className="font-semibold text-sm pt-2 text-content-strong">
                        {translate('personalSmartInbox.connectedChannel')}
                    </span>

                    {Object.entries(groupedChannels).map(([type, names]) => {
                        return (
                            <div key={type} className="pt-2">
                                <span className="text-xs/8 font-normal my-2 text-content-medium">
                                    {translate(`personalSmartInbox.${type}`)}
                                </span>
                                {(names as any[]).map((name, i) => (
                                    <span key={`${type}-${i}`} className="block text-sm text-content-strong">
                                        {name}
                                    </span>
                                ))}
                            </div>
                        );
                    })}
                </div>
            )}

            {isAdminConsentGranted && isConnected && (
                <form onSubmit={excludedContentForm.handleSubmit(handleSaveExcludedContent)} className="contents">
                    <div className="w-full flex flex-col">
                        <span className="font-semibold text-sm pt-2 text-content-strong">
                            {translate('personalSmartInbox.excludedContent')}
                        </span>
                        <span className="font-normal text-sm pt-2 text-content-strong">
                            {translate('personalSmartInbox.excludedContentDescription')}
                        </span>
                    </div>
                    <Controller
                        name="excludedContent.keywords"
                        control={excludedContentForm.control}
                        render={({ field }) => (
                            <FormField label={translate('personalSmartInbox.excludedContentForm.keywords')}>
                                <TagsField
                                    value={field.value || []}
                                    onValueChange={field.onChange}
                                    onBlur={field.onBlur}
                                    disabled={statusChanging}
                                    placeholder={translate(
                                        'personalSmartInbox.excludedContentForm.keywordsPlaceholder'
                                    )}
                                />
                            </FormField>
                        )}
                    />

                    <Controller
                        name="excludedContent.emailsDomains"
                        control={excludedContentForm.control}
                        rules={{
                            validate: (tags: string[]) => {
                                return (
                                    (tags || []).every((tag) => emailsAndDomainsRegex.test(tag)) ||
                                    translate('personalSmartInbox.excludedContentForm.invalidEmailOrDomain')
                                );
                            },
                        }}
                        render={({ field, fieldState }) => (
                            <FormField
                                label={translate('personalSmartInbox.excludedContentForm.emailsDomains')}
                                error={fieldState.error}
                            >
                                <TagsField
                                    value={field.value || []}
                                    onValueChange={field.onChange}
                                    onBlur={field.onBlur}
                                    disabled={statusChanging}
                                    placeholder={translate(
                                        'personalSmartInbox.excludedContentForm.emailsDomainsPlaceholder'
                                    )}
                                />
                            </FormField>
                        )}
                    />
                    <div className="flex justify-end">
                        <Button type="submit" variant="primary" disabled={!excludedContentForm.formState.isValid}>
                            {translate('personalSmartInbox.excludedContentForm.saveButton')}
                        </Button>
                    </div>
                </form>
            )}

            {isAdminConsentGranted && connectedAgentName && (
                <div className="w-full">
                    <span className="font-semibold text-sm pt-2 text-content-strong">
                        {translate('personalSmartInbox.connectedAgent')}
                    </span>
                    <div className="pt-2">
                        <span className="block text-sm text-content-strong">{connectedAgentName}</span>
                    </div>
                </div>
            )}
            {/* --- Modals --- */}
            <PersonalSmartInboxModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                connectOutlook={connectOutlook}
                channelForm={channelForm}
                hasErrorIntegration={hasErrorIntegration}
                setHasErrorIntegration={setHasErrorIntegration}
                searchParams={searchParams}
                setSearchParams={setSearchParams}
                onSetupSuccess={refetchPersonalMailboxStatus}
            />

            {hasErrorIntegration && (
                <ConfirmModal
                    onConfirm={() => {
                        handleOpenModal();
                        setHasErrorIntegration(false);
                        setSearchParams(new URLSearchParams());
                    }}
                    onCancel={() => {
                        setHasErrorIntegration(false);
                        setSearchParams(new URLSearchParams());
                    }}
                    title={translate('personalSmartInbox.errorTitle')}
                    labelConfirm={translate('personalSmartInbox.tryAgainButton')}
                >
                    {translate('personalSmartInbox.errorMessage')}
                </ConfirmModal>
            )}

            {isDisconnecting && (
                <ConfirmModal
                    onConfirm={async () => {
                        handleDisconnect(authForm);
                        await disconnectMailbox();
                    }}
                    onCancel={() => setIsDisconnecting(false)}
                    title={translate('personalSmartInbox.disconnectModalTitle')}
                    labelConfirm={translate('personalSmartInbox.disconnectButtonText')}
                    danger
                >
                    {translate('personalSmartInbox.disconnectModalDescription')}
                </ConfirmModal>
            )}
        </section>
    );
};

export default PersonalSmartInboxIntegration;
